import { CalendarMonth } from "@mui/icons-material";
import {
  Box,
  CircularProgress,
  Divider,
  Stack,
  Typography,
} from "@mui/material";
import { memo, useCallback, useEffect, useMemo, useState } from "react";
import NoData from "../../../components/UI/NoData";
import useFetch from "../../../hooks/useFetch";
import {
  ICalendarIdentifiers,
  ISectionData,
} from "../../../models/doctor-agenda";
import DoctorAgendaService from "../../../services/doctorAgenda.service";
import AppointmentCard from "../AppointmentCard";
import ScheduleTypeIcon, { SCHEDULE_TYPE_LIST } from "../ScheduleTypeIcon";
import { useDoctorAgenda } from "./useDoctorAgenda";
import { useTranslation } from "react-i18next";
import { InfoButton } from "../InfoButton";

interface IDoctorAgendaListThreeDaysProps {
  calendarDateStart: Date;
  calendarDateEnd: Date;
  showFree?: boolean;
}

const translation_path = "page.doctorAgenda.";

const doctorAgendaService = new DoctorAgendaService();

const capitalizeDateString = (dateString: string): string => {
  return dateString
    .split(" ")
    .map((word) =>
      word.replace(/(\b[a-z](?=[a-z]{2}))/g, (letter) => letter.toUpperCase())
    )
    .join(" ");
};

export const DoctorAgendaListThreeDays: React.FC<IDoctorAgendaListThreeDaysProps> =
  memo(({ calendarDateStart, calendarDateEnd, showFree }) => {
    const { t, i18n } = useTranslation();

    const { sendRequest } = useFetch(doctorAgendaService.getAll);

    const { getBrazilianDateISO, dayjsBrazil, icons } = useDoctorAgenda();

    const [appointments, setAppointments] = useState<ISectionData[]>([]);

    const [calendarIdentifiers, setCalendarIdentifiers] = useState<
      ICalendarIdentifiers[]
    >([]);

    const [loading, setloading] = useState(true);

    const formattedDateRange = useMemo(() => {
      const start = dayjsBrazil(calendarDateStart).format("DD/MM/YYYY");
      const end = dayjsBrazil(calendarDateEnd).format("DD/MM/YYYY");

      return `${start} - ${end}`;
    }, [calendarDateStart, calendarDateEnd, dayjsBrazil]);

    const fetchAppointments = useCallback(async () => {
      const { data, success } = await sendRequest({
        CalendarDateStart: getBrazilianDateISO(calendarDateStart),
        CalendarDateEnd: getBrazilianDateISO(calendarDateEnd),
        ShowFree: showFree,
      });

      if (success && data) {
        const sortedData = data.sort((a, b) => {
          const dateA = new Date(a.calendarStartDate);
          const dateB = new Date(b.calendarStartDate);
          return dateA.getTime() - dateB.getTime();
        });
        const identifiersMap = new Map<string | number, ICalendarIdentifiers>();
        sortedData.forEach((item) => {
          const newItem = {
            calendarIdentifier: item.calendarIdentifier,
            legend: item.calendarShortName || item.descriptionSite,
          };
          identifiersMap.set(item.calendarIdentifier, newItem);
        });
        const uniqueCalendarIdentifiers = Array.from(identifiersMap.values());
        const groupedData: ISectionData[] = sortedData.reduce(
          (acc, appointment) => {
            const date = new Date(appointment.calendarDate).toLocaleDateString(
              i18n.language === "pt-BR" ? "pt-BR" : "en-US",
              {
                weekday: "long",
                year: "numeric",
                month: "long",
                day: "numeric",
              }
            );

            const formattedTitle = capitalizeDateString(date);
            const section = acc.findIndex((s) => s.title === formattedTitle);
            if (section >= 0) {
              acc[section].data.push(appointment);
            } else {
              acc.push({ title: formattedTitle, data: [appointment] });
            }
            return acc;
          },
          [] as ISectionData[]
        );

        setCalendarIdentifiers(uniqueCalendarIdentifiers);
        setAppointments(groupedData);
      }
      setloading(false);
    }, [
      sendRequest,
      getBrazilianDateISO,
      calendarDateStart,
      calendarDateEnd,
      i18n.language,
      showFree,
    ]);

    useEffect(() => {
      fetchAppointments();
    }, [fetchAppointments]);

    if (loading) {
      return (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            width: "100%",
            py: 5,
          }}
        >
          <CircularProgress />
        </Box>
      );
    }

    return (
      <Stack spacing={2}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Box
            display="inline-flex"
            alignItems="center"
            sx={{
              alignSelf: "flex-start",
              backgroundColor: "transparent",
              padding: 1.5,
              borderRadius: 20,
              borderWidth: 1,
              borderColor: "primary.main",
              borderStyle: "solid",
            }}
          >
            <CalendarMonth color="primary" />
            <Typography sx={{ fontSize: 16, fontWeight: "bold", ml: 1 }}>
              {formattedDateRange}
            </Typography>
          </Box>

          <InfoButton
            title={t(translation_path + "info_title")}
            content={
              <>
                {SCHEDULE_TYPE_LIST.map((type) => (
                  <Box
                    key={type.code}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      paddingY: 1,
                    }}
                  >
                    <ScheduleTypeIcon
                      tasyScheduleTypeCode={type.code}
                      size={32}
                      disableTooltip
                    />
                    <Typography
                      variant="body1"
                      sx={{
                        marginLeft: 2,
                        fontSize: 16,
                        color: "text.primary",
                        flexShrink: 1,
                      }}
                    >
                      {t(
                        translation_path +
                          "scheduleType." +
                          type.translationKey
                      )}
                    </Typography>
                  </Box>
                ))}

                <Divider sx={{ marginY: 1 }} />

                {calendarIdentifiers.length > 0 ? (
                  calendarIdentifiers.map((identifier, index) => {
                    const icon = icons[index];
                    if (!icon) return null;

                    return (
                      <Box
                        key={identifier.calendarIdentifier}
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          paddingY: 1,
                        }}
                      >
                        {icon}
                        <Typography
                          variant="body1"
                          sx={{
                            marginLeft: 2,
                            fontSize: 16,
                            color: "text.primary",
                            flexShrink: 1,
                          }}
                        >
                          {identifier.legend ||
                            t(translation_path + "not_informed")}
                        </Typography>
                      </Box>
                    );
                  })
                ) : (
                  <Typography
                    variant="body1"
                    sx={{
                      marginLeft: 2,
                      fontSize: 16,
                      color: "text.primary",
                      flexShrink: 1,
                    }}
                  >
                    {t(translation_path + "no_legend")}
                  </Typography>
                )}
              </>
            }
          />
        </Box>

        {appointments.length > 0 ? (
          appointments.map(({ data, title }, index) => {
            return (
              <Stack key={index} spacing={2}>
                <Box sx={{ position: "relative", paddingY: "1em" }}>
                  <Divider>
                    <Typography
                      variant="caption"
                      color="text.primary"
                      fontWeight="bold"
                      fontSize={16}
                    >
                      {title}
                    </Typography>
                  </Divider>
                </Box>

                {data.map((item) => {
                  const appointmentDate = new Date(item.calendarStartDate);
                  const hour = appointmentDate.toLocaleTimeString("pt-BR", {
                    hour: "2-digit",
                    minute: "2-digit",
                  });
                  const iconPosition = calendarIdentifiers.findIndex(
                    (i) => i.calendarIdentifier === item.calendarIdentifier
                  );
                  const icon = icons[iconPosition];

                  return (
                    <AppointmentCard
                      key={item.identifier}
                      appointment={item}
                      icon={icon}
                      hour={hour}
                    />
                  );
                })}
              </Stack>
            );
          })
        ) : (
          <NoData message={t(translation_path + "no_appointment")} />
        )}
      </Stack>
    );
  });
